import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BlockhashWithExpiryBlockHeight,
  Commitment,
  PublicKey,
  SimulatedTransactionResponse,
  Transaction
} from '@solana/web3.js';
import { NeonAddress, SPLToken } from '@neonevm/token-transfer-core';
import { Observable } from 'rxjs';
import { encode } from 'bs58';
import {
  NeonBalance,
  NeonTransactionsResponse,
  SolanaBalance,
  SolanaEnvironment,
  SolanaTransactionResponse
} from './models';
import { environment } from '../environments/environment';
import { Amount, PriorityFeesResponse } from '../models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private _url: string = `${environment.urls.neonpassBackend}/api/v1`;
  private network: SolanaEnvironment = environment.network as SolanaEnvironment;

  solanaBalances(wallet: PublicKey): Observable<SolanaBalance[]> {
    return this.http.post<SolanaBalance[]>(`${this._url}/solana/balances`, {
      wallet: wallet.toBase58(),
      network: this.network
    });
  }

  solanaBalance(wallet: PublicKey, tokenMint: PublicKey): Observable<SolanaBalance> {
    return this.http.post<SolanaBalance>(`${this._url}/solana/balance`, {
      wallet: wallet.toBase58(),
      tokenMint: tokenMint.toBase58(),
      network: this.network
    });
  }

  walletBalance(wallet: PublicKey): Observable<number> {
    return this.http.post<number>(`${this._url}/solana/balance/wallet`, {
      wallet: wallet.toBase58(),
      network: this.network
    });
  }

  neonBalances(neonWallet: NeonAddress): Observable<NeonBalance[]> {
    return this.http.post<NeonBalance[]>(`${this._url}/neon/balances`, { neonWallet, network: this.network });
  }

  neonBalance(neonWallet: NeonAddress, tokenAddress: NeonAddress): Observable<NeonBalance> {
    return this.http.post<NeonBalance>(`${this._url}/neon/balance`, {
      neonWallet,
      tokenAddress,
      network: this.network
    });
  }

  solanaTransferSplToken(solanaWallet: PublicKey, neonWallet: string, tokenMint: PublicKey, amount: Amount): Observable<SolanaTransactionResponse> {
    return this.http.post<SolanaTransactionResponse>(`${this._url}/solana/transfer`, {
      solanaWallet: solanaWallet.toBase58(),
      tokenMint: tokenMint.toBase58(),
      network: this.network,
      neonWallet,
      amount
    });
  }

  solanaTransferWSolToken(solanaWallet: PublicKey, neonWallet: string, tokenMint: PublicKey, amount: Amount): Observable<SolanaTransactionResponse> {
    return this.http.post<SolanaTransactionResponse>(`${this._url}/solana/transfer/wsol`, {
      solanaWallet: solanaWallet.toBase58(),
      tokenMint: tokenMint.toBase58(),
      network: this.network,
      neonWallet,
      amount
    });
  }

  solanaTransferNeonToken(solanaWallet: PublicKey, neonWallet: string, amount: Amount): Observable<SolanaTransactionResponse> {
    return this.http.post<SolanaTransactionResponse>(`${this._url}/solana/transfer/neon`, {
      solanaWallet: solanaWallet.toBase58(),
      network: this.network,
      neonWallet,
      amount
    });
  }

  solanaTransferNeonTokenWithNeonFee(solanaWallet: PublicKey, serviceWallet: PublicKey, neonWallet: string, amount: Amount, rewardAmount: Amount): Observable<SolanaTransactionResponse> {
    return this.http.post<SolanaTransactionResponse>(`${this._url}/solana/transfer/neon_fee`, {
      network: this.network,
      solanaWallet: solanaWallet.toBase58(),
      serviceWallet: serviceWallet.toBase58(),
      neonWallet,
      amount,
      rewardAmount
    });
  }

  solanaTransactionFee(trx: Transaction): Observable<PriorityFeesResponse> {
    const transaction = encode(trx.serialize({ requireAllSignatures: false }));
    return this.http.post<PriorityFeesResponse>(`${this._url}/solana/fee`, { transaction });
  }

  solanaBlockHash(commitment: Commitment = 'confirmed'): Observable<BlockhashWithExpiryBlockHeight> {
    return this.http.post<BlockhashWithExpiryBlockHeight>(`${this._url}/solana/blockhash`, {
      network: this.network,
      commitment
    });
  }

  solanaSimulate(trx: Transaction, commitment: Commitment = 'confirmed'): Observable<SimulatedTransactionResponse> {
    const transaction = encode(trx.serialize({ requireAllSignatures: false }));
    return this.http.post<SimulatedTransactionResponse>(`${this._url}/solana/simulate`, {
      network: this.network,
      transaction,
      commitment
    });
  }

  neonTransferSplToken(neonWallet: string, solanaWallet: PublicKey, tokenMint: PublicKey, amount: Amount): Observable<NeonTransactionsResponse> {
    return this.http.post<NeonTransactionsResponse>(`${this._url}/neon/transfer/spl`, {
      solanaWallet: solanaWallet.toBase58(),
      tokenMint: tokenMint.toBase58(),
      network: this.network,
      neonWallet,
      amount
    });
  }

  neonTransferNeonToken(neonWallet: string, solanaWallet: PublicKey, amount: Amount): Observable<NeonTransactionsResponse> {
    return this.http.post<NeonTransactionsResponse>(`${this._url}/neon/transfer/neon`, {
      solanaWallet: solanaWallet.toBase58(),
      network: this.network,
      neonWallet,
      amount
    });
  }

  tokens(): Observable<SPLToken[]> {
    const params = { network: this.network };
    return this.http.get<SPLToken[]>(`${this._url}/tokens`, { params });
  }

  token(q: string): Observable<SPLToken> {
    const params = { q, network: this.network };
    return this.http.get<SPLToken>(`${this._url}/tokens/token`, { params });
  }

  constructor(private http: HttpClient) {
  }
}
